import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { LINKS } from '../../../../../Routs/config';
import { SearchInput } from '../../../../../generic/ui/SearchInput';
import { AppModal } from '../../../../../generic/ui/Modal';
import { createGroupColorMap } from '../../../../../generic/ui/colors';
import {
    ICustomerItem,
    IOrderItem,
} from '../../../../../modules/API/Drivers/models/kavData';
import CustomerCard from '../Components/CustomerCard';
import DriversActionDock from '../Components/DriversActionDock';
import DriversModals from '../Components/DriversModals';
import DriversPageLayout from '../Components/DriversPageLayout';
import DriversScrollArea from '../Components/DriversScrollArea';
import KavCard from '../Components/KavCard';
import PalletsModal from '../Components/PalletsModal';
import { filterCustomers } from '../helpers/driversFilter';
import { useDrivers } from '../services/DriversContext';

const KavCustomersPage: React.FC = () => {
    const navigate = useNavigate();
    const { areaId } = useParams<{ areaId: string }>();
    const {
        data,
        isOffice,
        isEditLocked,
        handleActionMovments,
        printLogPdf,
        printPdf,
        getPackageStatisticByKav,
        selectDataForPackageOrder,
        selectOrderForCloseDelivery,
        handleSelectProblem,
        openPhotoUpload,
        isPhotoUploaded,
    } = useDrivers();

    const [customerInput, setCustomerInput] = useState('');
    const [selectedOrder, setSelectedOrder] = useState<IOrderItem | null>(null);

    const kav = useMemo(
        () => data?.find((item) => item.AreaID === Number(areaId)) ?? null,
        [data, areaId],
    );

    const renderCustomers = useMemo(
        () => (kav ? filterCustomers(kav.Customers, customerInput) : []),
        [kav, customerInput],
    );

    const groupColorOf = useMemo(
        () =>
            createGroupColorMap(
                kav?.Customers.flatMap((customer) =>
                    customer.Orders.map((order) => order.GroupDesc),
                ) ?? [],
            ),
        [kav],
    );

    const handleBack = () => navigate(LINKS.drivers.quests.mainURL);

    const handlePackageOrder = (customer: ICustomerItem) => {
        if (!kav || isEditLocked) return;
        selectDataForPackageOrder(customer, kav.AreaID);
        // Относительная навигация: PackageOverlay читает ?id из location.search,
        // а путь /:areaId остаётся текущим.
        if (customer.PO_Num !== 0) {
            navigate({ search: `?id=${customer.PO_Num}` });
        }
    };

    // Единственный вход в selectOrderForCloseDelivery — модалка подтверждения
    // на прошедшей дате для WORKER не открывается.
    const handleConfirmDelivery = (customer: ICustomerItem) => {
        if (!kav || isEditLocked) return;
        selectOrderForCloseDelivery(customer.Orders, customer, kav.AreaID);
    };

    const handleProblem = (customer: ICustomerItem) => {
        if (!kav || isEditLocked) return;
        handleSelectProblem({
            kavID: kav.AreaID,
            CustomerID: customer.CustomerID,
            item: customer.Orders.filter((order) => order.IsProblem),
        });
    };

    const palletsTitle = selectedOrder
        ? selectedOrder.Delivery_Num !== 0
            ? `${selectedOrder.Delivery_Num}`
            : 'לא קיימת תעודה'
        : '';

    return (
        <DriversPageLayout title={kav?.AreaDesc?.trim() ?? ''}>
            <DriversModals />

            {selectedOrder && (
                <AppModal
                    title={palletsTitle}
                    Children={PalletsModal}
                    childrenProps={{ order: selectedOrder }}
                    controlFunc={() => setSelectedOrder(null)}
                    size='md'
                />
            )}

            <DriversScrollArea
                scrollHeader={
                    kav ? (
                        <KavCard
                            item={kav}
                            isOffice={isOffice}
                            variant='header'
                            onAction={(kavID, action) => {
                                if (!isEditLocked) handleActionMovments(kavID, action);
                            }}
                            onPrintLog={printLogPdf}
                            onOpenStatistic={getPackageStatisticByKav}
                            isActionLocked={isEditLocked}
                        />
                    ) : null
                }
                stickyHeader={
                    <Toolbar>
                        <SearchInput
                            value={customerInput}
                            onChange={(e) => setCustomerInput(e.target.value)}
                            placeholder='חיפוש לקוח'
                        />
                    </Toolbar>
                }
            >
                {!kav ? (
                    <EmptyState>
                        <img src='/images/no-data.png' alt='empty data' />
                    </EmptyState>
                ) : (
                    renderCustomers.map((customer) => (
                        <CustomerCard
                            key={customer.CustomerID}
                            customer={customer}
                            areaID={kav.AreaID}
                            isOffice={isOffice}
                            groupColorOf={groupColorOf}
                            onPackageOrder={handlePackageOrder}
                            onConfirmDelivery={handleConfirmDelivery}
                            onSelectProblem={handleProblem}
                            onOpenPallets={(order) => setSelectedOrder(order)}
                            onPrint={printPdf}
                            onPhoto={(ref) => {
                                if (!isEditLocked) openPhotoUpload(ref);
                            }}
                            isPhotoUploaded={isPhotoUploaded}
                            isEditLocked={isEditLocked}
                        />
                    ))
                )}
            </DriversScrollArea>

            <DriversActionDock onBack={handleBack} />
        </DriversPageLayout>
    );
};

export default KavCustomersPage;

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const EmptyState = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem 0;

    img {
        max-width: 60%;
        opacity: 0.9;
    }
`;
